import { useAuthStore } from '@/stores/auth.store';
import { keycloakRefresh } from './keycloak';

// Refresh this far before the access token actually expires. Generous enough to
// absorb clock skew and the round-trip to Keycloak.
const REFRESH_BUFFER_MS = 60_000;

// A single in-flight refresh shared by all callers (the request interceptor, the
// 401 retry path, and the background loop) so we never fire concurrent refreshes.
let refreshPromise: Promise<boolean> | null = null;

/**
 * Exchange the refresh token for a fresh access token (deduplicated). Returns
 * `true` on success. Does NOT log out - the caller decides what a failure means
 * (a 401 retry gives up; the background loop ends the session).
 *
 * Exported for the SSE streams, which cannot go through `authFetch`: they own
 * their connection and must refresh-then-reconnect themselves. Going through
 * this shared promise is what keeps two streams hitting 401 in the same instant
 * from firing two refreshes. The new token lands in the auth store - read it
 * from there rather than expecting it back here.
 */
export async function runRefresh(): Promise<boolean> {
  const { refreshToken, setTokens } = useAuthStore.getState();
  if (!refreshToken) return false;
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const data = await keycloakRefresh(refreshToken);
      setTokens(data.access_token, data.expires_in, data.refresh_token);
      return true;
    } catch {
      return false;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

/** Refresh only when the access token is within the buffer of expiring. */
export async function refreshIfNeeded(): Promise<void> {
  const { accessToken, refreshToken, expiresAt } = useAuthStore.getState();
  if (!accessToken || !refreshToken || !expiresAt) return;
  if (Date.now() < expiresAt - REFRESH_BUFFER_MS) return;
  await runRefresh();
}

const sessionExpiryResponses = new WeakSet<Response>();

export function isSessionExpiryResponse(response: Response): boolean {
  return sessionExpiryResponses.has(response);
}

function sendWithToken(request: Request): Promise<Response> {
  const { accessToken } = useAuthStore.getState();
  const attempt = request.clone();
  if (accessToken) attempt.headers.set('Authorization', `Bearer ${accessToken}`);
  return fetch(attempt);
}

/**
 * Fetch wrapper that keeps the session alive:
 *   1. proactively refreshes if the token is about to expire,
 *   2. on a 401, refreshes once and RETRIES the request,
 *   3. only logs out if that retry still fails.
 *
 * Step 2 is the important one - a 401 can happen the instant the token expires
 * (clock skew, a request racing the proactive buffer, a token revoked mid-flight).
 * Previously any 401 logged the user straight out; now it self-heals.
 */
export async function authFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  await refreshIfNeeded();

  const request = new Request(input, init);
  let response = await sendWithToken(request);

  if (response.status === 401) {
    const refreshed = await runRefresh();
    if (refreshed) {
      response = await sendWithToken(request); // retry once with the new token
    } else {
      sessionExpiryResponses.add(response);
    }
    if (response.status === 401) {
      useAuthStore.getState().logout();
    }
  }

  return response;
}

// ─────────────────────────────────────────────────────────────────────────────
// Background refresh loop
//
// Refresh just before the access token expires even when the user is idle. Each
// refresh also resets Keycloak's SSO-idle timer, so an open tab stays signed in
// up to the SSO *max* lifespan instead of being bounced to the login screen after
// an idle gap. The store subscription re-arms the timer after every token change
// (login / refresh) and clears it on logout.
// ─────────────────────────────────────────────────────────────────────────────
let refreshTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleProactiveRefresh(): void {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
  const { isAuthenticated, expiresAt } = useAuthStore.getState();
  if (!isAuthenticated || !expiresAt) return;

  const delay = Math.max(1_000, expiresAt - Date.now() - REFRESH_BUFFER_MS);
  refreshTimer = setTimeout(() => {
    void (async () => {
      const ok = await runRefresh();
      if (!ok) useAuthStore.getState().logout();
      // A successful refresh updates the store → the subscription below re-arms
      // the timer for the new expiry.
    })();
  }, delay);
}

/** Start the background refresh loop. Call once at app startup. */
export function startAuthRefreshLoop(): void {
  scheduleProactiveRefresh();
  useAuthStore.subscribe(scheduleProactiveRefresh);
}
